// src/UserProfilePanel.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Avatar, Typography, Chip, Stack,
  Button, Switch, Divider, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FreelancerAlertsPanel from './FreelancerAlertsPanel';
import Paper from '@mui/material/Paper';
import { busyDaysFromProduzioni } from './lib/calendarUtils';
import { normalizeProduction } from './utils/maestranze';
import { normalizeSkills } from './utils/normalize';
import ProductionAssistantPanel from './components/ProductionAssistantPanel';
import { setFavoriteFreelancer, upsertAvailabilityAlert, setPriorityFreelancer, fetchJson } from './api';
import { useTranslation } from 'react-i18next';
import SquadraModal from './SquadraModal';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { buildAISummary } from './utils/aiSummary';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LinearProgress } from '@mui/material';
import TeamMiniPanel from './components/TeamMiniPanel';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';


// helpers date & telefono (niente slittamenti)
const toDateOnly = (s) => {
  if (!s) return '';
  const str = String(s);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(str); if (isNaN(d)) return '';
  const y = d.getFullYear(); const mo = String(d.getMonth()+1).padStart(2,'0'); const da = String(d.getDate()).padStart(2,'0');
  return `${y}-${mo}-${da}`;
};
const formatIT = (s) => {
  const ymd = toDateOnly(s); if (!ymd) return '';
  const [y,m,d] = ymd.split('-'); return `${d}/${m}/${y}`;
};
const formatPhonePretty = (s) => {
  if (!s) return '';
  const d = String(s).replace(/\D/g,'');
  let local = d;
  if (d.startsWith('0039')) {
    local = d.slice(4);
  } else if (d.startsWith('39') && (d.length === 12 || d.length === 11)) {
    local = d.slice(2);
  }
  if (local.length === 10) return '+39 ' + local.slice(0,3) + '.' + local.slice(3,6) + '.' + local.slice(6);
  if (d.length === 10) return d.slice(0,3) + '.' + d.slice(3,6) + '.' + d.slice(6);
  return s;
};


// ---- Config ---------------------------------------------------------------
const API_BASE = (() => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  // se qualcuno ha messo 5000 in .env, forziamo 3001 o proxy /api
  if (/^https?:\/\/localhost:5000/i.test(env)) return 'http://localhost:3001/api';
  // in dev usa il proxy CRA se REACT_APP_API_BASE è vuoto
  return (env || '/api');
})().replace(/\/+$/, '');

const API_ORIGIN = (() => {
  try { return new URL(API_BASE, window.location.origin).origin; }
  catch { return window.location.origin; }
})();


// ---- Helpers --------------------------------------------------------------

// --- Role/Skill/Company helpers --------------------------------------------
const norm = (s) => (s || '').toString().trim().toLowerCase();

function normalizeRole(r) {
  const x = norm(r);
  if (!x) return '';
  if (/finalizzatore|finisher|finalizer|finishing/.test(x)) return 'finalizzatore';
  if (/editor(?!ial)|montator/.test(x)) return 'editor';
  if (/color(ist| grading)|davinci/.test(x)) return 'colorist';
  if (/assist(ente)?\s?(montaggio|editor)/.test(x)) return 'assistant_editor';
  return x;
}

function resolveMainRoleFromProfile(p) {
  return normalizeRole(
    p?.ruolo_principale ||
    (Array.isArray(p?.ruoli) ? p.ruoli[0] : null) ||
    p?.role || p?.mansione || p?.titolo || ''
  );
}

function companyDomainFromName(name='') {
  const s = norm(name);
  if (!s) return 'unknown';
  if (/(banijay|fremantle|endemol|rai|mediaset|sky|discovery|warner|wbd|magnolia)/.test(s)) return 'tv_format';
  if (/(adv|agency|pubblicit|spot|campaign)/.test(s)) return 'adv';
  if (/(wedding|matrimonio)/.test(s)) return 'wedding';
  if (/(corporate|istituzionale|evento|convention)/.test(s)) return 'corporate';
  if (/(film|cinema)/.test(s)) return 'film';
  if (/(documentar)/.test(s)) return 'doc';
  if (/(digital|social|youtube|tiktok|reels)/.test(s)) return 'digital';
  return 'unknown';
}

function normalizeSkillSet(list=[]) {
  // riusa la tua normalizeSkills già importata, poi metti in Set
  const arr = normalizeSkills(list || []);
  return new Set(arr.map(norm));
}

// ultimo fine-contratto negli ultimi N mesi?
function hasRecentWork(produzioni=[], months=12) {
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - months, now.getDate(), 12, 0, 0, 0).getTime();
  const toLocalNoon = (raw) => {
    if (!raw) return null;
    const ymd = toDateOnly(raw);
    if (!ymd) return null;
    const [y,m,d] = ymd.split('-').map(Number);
    return new Date(y, m-1, d, 12, 0, 0, 0).getTime();
  };
  return (produzioni || []).some(p => {
    const t = toLocalNoon(p?.data_fine_contratto || p?.data_fine);
    return (t != null && t >= cutoff);
  });
}


const msInDay = 1000 * 60 * 60 * 24;

// ancora “mezzogiorno UTC” per evitare slittamenti
const todayNoonUTC = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12, 0, 0, 0));
};
const toUtcNoon = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).slice(0,10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
};

const isActiveContract = (end) => {
  const ymd = toDateOnly(end);
  const endNoon = toUtcNoon(ymd);
  if (!endNoon) return false;
  // intervallo chiuso: se il contratto “finisce il 28”, quel giorno è ancora occupato
  return endNoon.getTime() >= todayNoonUTC().getTime();
};

const daysUntil = (dateStr) => {
  const ymd = toDateOnly(dateStr);
  const endNoon = toUtcNoon(ymd);
  if (!endNoon) return Infinity;
  const diffMs = endNoon.getTime() - todayNoonUTC().getTime();
  // floor + clamp(0) per evitare il classico +1 quando sei vicino alla mezzanotte/fuso
  return Math.max(0, Math.floor(diffMs / msInDay));
};

// === AMBITO (ex Dominio) ==========================================
const _norm = (s) => (s || '').toString().trim().toLowerCase();

function classifyDomainFromTitleAndMeta(p = {}) {
  const t = _norm(p.produzione || p.titolo || p.title || '');
  const az = _norm(p.azienda || p.company || p.producer || p.network || '');
  const tags = (p.tags || []).map(_norm).join(' ');
  const blob = `${t} ${az} ${tags}`;

  if (/(masterchef|x-?factor|grande fratello|isola dei famosi|pechino|bake off|temptation|talent|reality|game\s*show|quiz|sitcom)/.test(blob)) return 'tv_format';
  if (/(wedding|matrimoni|sposa|cerimonia)/.test(blob)) return 'wedding';
  if (/(videoclip|music video|lyric video)/.test(blob)) return 'music_video';
  if (/(corporate|azienda|istituzionale|convention|evento|fiera)/.test(blob)) return 'corporate';
  if (/(spot|adv|pubblicit[aà]|campaign|campagna)/.test(blob)) return 'adv';
  if (/(film|feature|lungometraggio)/.test(blob)) return 'film';
  if (/(docu|documentar)/.test(blob)) return 'doc';
  if (/(social|short form|tiktok|reels|youtube|branded content)/.test(blob)) return 'digital';
  if (/(banijay|fremantle|endemol|magnolia|rai|mediaset|sky|discovery|wbd)/.test(blob)) return 'tv_format';
  return 'unknown';
}

function domainDistribution(prods = []) {
  const dist = {};
  for (const p of prods || []) {
    const d = classifyDomainFromTitleAndMeta(p);
    if (d === 'unknown') continue; // ignora domini non classificati
    dist[d] = (dist[d] || 0) + 1;
  }
  const tot = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
  Object.keys(dist).forEach(k => dist[k] = dist[k] / tot);
  return dist;
}

function humanizeDomain(key) {
  switch (key) {
    case 'tv_format':   return 'TV format';
    case 'wedding':     return 'Matrimoni';
    case 'music_video': return 'Videoclip';
    case 'corporate':   return 'Corporate';
    case 'adv':         return 'ADV / Spot';
    case 'film':        return 'Film';
    case 'doc':         return 'Documentari';
    case 'digital':     return 'Digital / Social';
    default:            return '—';
  }
}



function resolveImgUrl(u) {
  if (!u) return '';
  const url = String(u);

  // Link assoluti: correzione porta legacy
  if (/^https?:\/\//i.test(url)) {
    return url.replace('localhost:5000', 'localhost:3001');
  }
  // Se è già un URL assoluto corretto, lascialo stare
  if (/^https?:\/\/(?!localhost)/i.test(url)) return url;

  // Link relativi: puntiamo sempre a /uploads su 3001
  if (url.startsWith('/uploads/')) return `${API_ORIGIN}${url}`;
  if (url.startsWith('uploads/'))   return `${API_ORIGIN}/${url}`;
  return `${API_ORIGIN}/uploads/${url.replace(/^\/+/, '')}`;
}




// Prova a trovare QUALSIASI ruolo nell'oggetto produzione (anche se la chiave cambia)
const sniffRole = (item) => {
  if (!item || typeof item !== 'object') return null;
  // 1) preferisci chiavi note
  const known =
    item.ruolo ?? item.my_role ?? item.freelancer_role ?? item.ruolo_freelance ??
    item.role ?? item.ruolo_produzione ?? item.mansione ??
    (Array.isArray(item.ruoli) && item.ruoli[0]) ??
    null;
  if (typeof known === 'string' && known.trim()) return known.trim();

  // 2) fallback: cerca QUALSIASI chiave che contenga 'ruol'
  for (const [k, v] of Object.entries(item)) {
    if (/ruol/i.test(k) && typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }

  // 3) annidato: se c'è un sotto-oggetto tipo { link: { ruolo: '...' } }
  for (const v of Object.values(item)) {
    if (v && typeof v === 'object') {
      const inner = sniffRole(v);
      if (inner) return inner;
    }
  }
  return null;
};

// Ruolo finale: prima quello per-produzione, poi fallback al profilo
const resolveRole = (item, prof) => (
  sniffRole(item) ||
  prof?.ruolo_principale ||
  (Array.isArray(prof?.ruoli) ? prof.ruoli[0] : null) ||
  null
);



// normalizza una produzione in { azienda, produzione, data_fine_contratto, ruolo_produzione, id }
const normalizeProductionLegacy = (p) => ({
    id: p.id || p._id || undefined,
    azienda: p.azienda || p.company || p.producer || p.network || '',
    produzione: p.produzione || p.titolo || p.title || p.show || p.program || '',
    ruolo_produzione: p.ruolo_produzione || p.role || p.position || p.job_title || '',
    // ⬇️ MANTENIAMO ANCHE L'INIZIO
    data_inizio_contratto:
      p.data_inizio_contratto ||
      p.start_date || p.startDate || p.contract_start || p.date_from || p.dal || '',
    data_fine_contratto:
      p.data_fine_contratto ||
      p.end_date || p.endDate || p.contract_end || p.date_to || p.al || ''
});

const getFreelancerIdFrom = (profile, endpointProfile) =>
  profile?.id || profile?._id || (endpointProfile?.match?.(/\/freelancers\/([^/]+)/)?.[1]) || null;

// Mini calendario compatto con colorazione libero/occupato (senza giorni fuori mese)
function MiniAvailabilityCalendar({ busyDays, tentativeDays }) {
  const [anchor, setAnchor] = React.useState(() => new Date()); // mese visibile
  const ymd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;

  const y = anchor.getFullYear();
  const m = anchor.getMonth();

  // 1) Solo giorni del mese corrente
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const daysInMonth = last.getDate();

  // offset per far partire dal lunedì (0=Mon ... 6=Sun)
  const mondayIndex = (first.getDay() + 6) % 7; // 0..6
  const dates = Array.from({ length: daysInMonth }, (_, i) => new Date(y, m, i + 1));

  const monthLabel = first.toLocaleDateString('it-IT', {
    month: 'long',
    year: 'numeric'
  });

  const isBusy = (d) => busyDays?.has?.(ymd(d));
  const isTentativeDay = (d) => !isBusy(d) && tentativeDays?.has?.(ymd(d));

  const goPrev = () => setAnchor(new Date(y, m - 1, 1));
  const goNext = () => setAnchor(new Date(y, m + 1, 1));

  // palette
  const clr = {
  border: 'rgba(212,165,80,0.2)',
  freeBg: 'rgba(76,175,80,0.18)',
  freeTx: '#81c784',
  busyBg: 'rgba(239,83,80,0.18)',
  busyTx: '#ef9a9a',
  tentBg: 'rgba(167,139,250,0.18)',
  tentTx: '#a78bfa',
};

  return (
    <div style={{
      width: 230, padding: 12, borderRadius: 12, border: `1px solid ${clr.border}`,
      background: '#1a1d23'
    }}>
      {/* header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
        <button onClick={goPrev} style={{ border:'none', background:'transparent', cursor:'pointer', color:'#d4a550' }}>‹</button>
        <div style={{ fontWeight: 600, fontSize: 14, textTransform:'capitalize', color:'#e0e0e0' }}>{monthLabel}</div>
        <button onClick={goNext} style={{ border:'none', background:'transparent', cursor:'pointer', color:'#d4a550' }}>›</button>
      </div>

      {/* giorni settimana */}
      <div style={{
        display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:6, marginBottom:6,
        fontSize:11, color:'#888', textAlign:'center'
      }}>
        {['L','M','M','G','V','S','D'].map((w, i) => <div key={`${w}-${i}`}>{w}</div>)}
      </div>

      {/* griglia giorni: solo mese corrente, con offset sulla prima cella */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:6 }}>
        {dates.map((d, idx) => {
          const busy = isBusy(d);
          const tentative = isTentativeDay(d);
          const bg = busy ? clr.busyBg : tentative ? clr.tentBg : clr.freeBg;
          const tx = busy ? clr.busyTx : tentative ? clr.tentTx : clr.freeTx;

          const extra = idx === 0 ? { gridColumnStart: mondayIndex + 1 } : null;

          return (
            <div
              key={ymd(d)}
              style={{
                height: 26, borderRadius: 8, background: bg, color: tx,
                fontSize: 12, display:'flex', alignItems:'center', justifyContent:'center',
                border: `1px solid ${clr.border}`,
                ...extra
              }}
              title={ymd(d)}
            >
              {d.getDate()}
            </div>
          );
        })}
      </div>

      {/* legenda */}
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, fontSize:11, color:'#6b7280' }}>
        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
          <span style={{ width:10, height:10, borderRadius:3, background: clr.freeBg, border:`1px solid ${clr.border}` }} />
          Libero
        </span>
        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
          <span style={{ width:10, height:10, borderRadius:3, background: clr.tentBg, border:`1px solid rgba(167,139,250,0.3)` }} />
          Non conf.
        </span>
        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
          <span style={{ width:10, height:10, borderRadius:3, background: clr.busyBg, border:`1px solid ${clr.border}` }} />
          Occupato
        </span>
      </div>
    </div>
  );
}


// Converte Date -> 'YYYY-MM-DD' locale
function toYmdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// Parsea qualsiasi campo data (stringa 'YYYY-MM-DD' o ISO) in Date (locale a mezzogiorno)
function parseAsLocalDate(any) {
  if (!any) return null;
  if (typeof any === 'string') {
    // se già 'YYYY-MM-DD'
    const m = any.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
    // altrimenti lascio fare a Date (ISO ecc.) ma poi porto a mezzogiorno locale
    const d = new Date(any);
    if (!isNaN(d)) return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
    return null;
  }
  if (any instanceof Date && !isNaN(any)) {
    return new Date(any.getFullYear(), any.getMonth(), any.getDate(), 12, 0, 0, 0);
  }
  return null;
}

// Estrae in modo robusto [start,end] da una produzione
function getRange(p) {
  const startRaw =
    p?.data_inizio_contratto ?? p?.data_inizio ?? p?.start ?? p?.inizio ?? p?.from;
  const endRaw =
    p?.data_fine_contratto ?? p?.data_fine ?? p?.end ?? p?.fine ?? p?.to;

  const s = parseAsLocalDate(startRaw);
  const e = parseAsLocalDate(endRaw);
  if (!s) return null;
  // se fine mancante, considero 1 giorno
  const end = e && !isNaN(e) ? e : s;
  return [s, end];
}

// Costruisce il Set di YYYY-MM-DD occupati (inclusivi)
function buildBusySetFromProduzioni(list) {
  const set = new Set();
  if (!Array.isArray(list)) return set;

  for (const p of list) {
    if (p.is_tentative) continue; // escludi produzioni non confermate
    const range = getRange(p);
    if (!range) continue;
    const [s, e] = range;
    // loop inclusivo da s a e
    const d = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 12, 0, 0, 0);
    const last = new Date(e.getFullYear(), e.getMonth(), e.getDate(), 12, 0, 0, 0);
    while (d <= last) {
      set.add(toYmdLocal(d));
      d.setDate(d.getDate() + 1);
    }
  }
  return set;
}




// ---- Component -------------------------------------------------------------
export default function UserProfilePanel({ notesSlot, collaborationsSlot,
  token,
  user,
  onEditProfile,
  onMessage,               // ✅ AGGIUNGI QUESTO
  initialProfile,
  busyDays,
  endpointProfile = '/me',
  endpointAvailability = '/me/availability',
  readOnly = false,
            viewerType = 'coordinator',   // 'coordinator' | 'freelance'
}) {

  const { t } = useTranslation();
  const [profile, setProfile] = useState(initialProfile || null);
  const [savingAvail, setSavingAvail] = useState(false);

  // fallback produzioni se il profilo non le contiene
  const [extraProductions, setExtraProductions] = useState([]);

  // Crediti CV collegati (sola lettura — arriva dall'API /cv/users/:userId/credits)
  const [creditsMap, setCreditsMap] = useState({});

  // Stato modal "Vedi squadra"
  const [squadraOpen, setSquadraOpen] = useState(false);
  const [squadraEditionId, setSquadraEditionId] = useState(null);
  const [squadraProgramName, setSquadraProgramName] = useState('');
  const [squadraEditionLabel, setSquadraEditionLabel] = useState('');

  // Fetch profilo dal backend
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}${endpointProfile}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error('Impossibile caricare il profilo');
        const data = await res.json();
        if (mounted) setProfile(data);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { mounted = false; };
  }, [token, endpointProfile]);

 // Se il profilo non ha produzioni, non chiamare endpoint inesistenti: usa solo quanto c'è
useEffect(() => {
  if (!profile) return;

  const raw = profile?.maestranze ?? profile?.produzioni ?? profile?.productions ?? [];
  if (Array.isArray(raw) && raw.length > 0) {
    setExtraProductions([]); // abbiamo già i dati giusti nel profilo
  } else {
    setExtraProductions([]); // nessun dato aggiuntivo disponibile
  }
}, [profile]);

// Fetch crediti CV dell'utente visualizzato (sola lettura)
useEffect(() => {
  if (!profile) return;
  const userId = profile?.user_id ?? profile?.id ?? null;
  if (!userId) return;

  let alive = true;
  fetchJson(`/cv/users/${userId}/credits`)
    .then(data => {
      if (!alive) return;
      const list = Array.isArray(data?.credits) ? data.credits : [];
      const map = {};
      list.forEach(c => {
        const ids = Array.isArray(c.source_maestranza_ids) && c.source_maestranza_ids.length
          ? c.source_maestranza_ids
          : c.source_maestranza_id != null ? [c.source_maestranza_id] : [];
        ids.forEach(id => { map[String(id)] = c; });
      });
      setCreditsMap(map);
    })
    .catch(err => {
      // Non blocca il pannello: la timeline funziona anche senza crediti
      console.warn('[UserProfilePanel] crediti CV non disponibili:', err?.message || err);
    });
  return () => { alive = false; };
}, [profile]);

     

  // Display name e headline
  const displayName = useMemo(() => {
    const n = [profile?.nome, profile?.cognome].filter(Boolean).join(' ');
    return n || profile?.display_name || user?.display_name || '';
  }, [profile, user]);

  const headline =
    profile?.headline ||
    profile?.ruolo_principale ||
    (user?.roles?.[0] || user?.primaryRole) ||
    '';

// Bio normalizzata dal profilo
const bioText = React.useMemo(() => {
  const s =
    (typeof profile?.bio === 'string' ? profile.bio : null) ??
    (typeof profile?.descrizione === 'string' ? profile.descrizione : null) ??
    '';
  return (s || '').trim();
}, [profile]);


  // Skill testuali (accetta array o stringa "a, b, c") + normalizzazione/dedup
const rawSkills =
  (Array.isArray(profile?.skills) ? profile.skills : null) ??
  (profile?.skillsText
    ? profile.skillsText.split(',').map(s => s.trim()).filter(Boolean)
    : null) ??
  (Array.isArray(profile?.abilita) ? profile.abilita : []) // eventuale alias backend

// ➜ 'premiere' -> 'Adobe Premiere Pro', case-insensitive, dedup
const skills = normalizeSkills(rawSkills || []).sort((a,b) => a.localeCompare(b));

  const [imgError, setImgError] = useState(false);
  const [showAllProds, setShowAllProds] = useState(false);
  React.useEffect(() => { setImgError(false); }, [profile?.profile_image_url, profile?.image_url]);

const avatarUrl = useMemo(() => resolveImgUrl(
  profile?.profile_image_url ||
  profile?.profileImageUrl ||
  profile?.image_url ||
  profile?.avatar_url ||
  profile?.avatar
), [profile]);


  // Contatti
  const phone = profile?.telefono || '';
  const email = profile?.email || user?.email || '';

  // Disponibilità (switch indipendente, solo dashboard)
  const isAvailable = !!profile?.is_available;

  const getMyId = () => (
  profile?.id ?? profile?.user_id ?? profile?.freelancer_id ?? null
);

const myFreelancerId = getMyId();
const myName = displayName || 'Freelance';

const handleToggleFavorite = async (enabled) => {
  if (!myFreelancerId) throw new Error('ID non disponibile');
  await setFavoriteFreelancer(myFreelancerId, enabled);
};

const handleUpsertAlert = async (leadDays) => {
  if (!myFreelancerId) throw new Error('ID non disponibile');
  await upsertAvailabilityAlert(myFreelancerId, leadDays);
};

const handleTogglePriority = async (enabled) => {
  if (!myFreelancerId) throw new Error('ID non disponibile');
  await setPriorityFreelancer(myFreelancerId, enabled);
};

const handleOpenSquadra = (credit) => {
  setSquadraEditionId(credit.edition_id);
  setSquadraProgramName(credit.program_name || '');
  setSquadraEditionLabel(credit.edition_label || '');
  setSquadraOpen(true);
};


  // Produzioni (dal profilo: chiave reale = maestranze)
  const prodList = useMemo(() => {
  const rawPrimary =
    profile?.maestranze ??
    profile?.produzioni ??
    profile?.productions ??
    [];
    // === Ambito prevalente (calcolato DOPO prodList) ===


     const source = (Array.isArray(rawPrimary) && rawPrimary.length > 0)
    ? rawPrimary
    : (Array.isArray(extraProductions) ? extraProductions : []);

  const myId = getMyId();
  const result = source.map(p => normalizeProduction(p, myId));
  return result;
}, [profile, extraProductions]);

// === Ambito prevalente (fuori dai callback, dopo prodList) ===
const dist = React.useMemo(() => domainDistribution(prodList || []), [prodList]);
const topDomain = React.useMemo(
  () => Object.entries(dist).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown',
  [dist]
);
const topDomainPct = React.useMemo(
  () => Math.round(((dist?.[topDomain] ?? 0) * 100)),
  [dist, topDomain]
);
const domainBreakdown = React.useMemo(() => {
  const arr = Object.entries(dist || {}) // es. [['tv_format', 0.82], ['corporate', 0.18]]
    .map(([key, val]) => ({ key, pct: Math.round(val * 100) }))
    .sort((a, b) => b.pct - a.pct);
  return arr;
}, [dist]);

// === Production context (casa di produzione corrente) =======================
const desiredRole =
  normalizeRole(
    user?.ruolo_richiesto || user?.desired_role || ''   // se lo passi dal profilo produzione
  ) || null;

const desiredSkills = Array.isArray(user?.desired_skills) ? user.desired_skills : ['premiere','davinci resolve','after effects']; // fallback visivo
const desiredSkillSet = normalizeSkillSet(desiredSkills);

const productionHouseDomain =
  user?.company_domain ||
  companyDomainFromName(user?.azienda || user?.company_name || '');

// === Profilo freelancer per l'affinità =====================================
const mainRole = resolveMainRoleFromProfile(profile);
const mySkillSet = normalizeSkillSet(skills); // 'skills' è già normalizzato sopra
const roleMatch = desiredRole ? (desiredRole === mainRole) : true;
const skillMatch = [...desiredSkillSet].every(s => mySkillSet.has(norm(s)));
const recencyBoost = hasRecentWork(prodList, 12);


const [aiSummaryText, setAiSummaryText] = React.useState('');
React.useEffect(() => {
  // Lazy: carica l'analisi AI dopo il render iniziale
  const timer = setTimeout(() => {
    const bio = profile?.bio || profile?.descrizione || '';
    const skills = Array.isArray(profile?.skills) ? profile.skills
                : Array.isArray(profile?.abilita) ? profile.abilita
                : [];
    const productions = prodList || [];
    const mainRole = profile?.ruolo || profile?.role || profile?.mansione || profile?.titolo || 'Post-producer';
    const seniority = profile?.seniority || (String(profile?.titolo || '').match(/senior|junior/i)?.[0] ?? '');
    setAiSummaryText(buildAISummary({ bio, skills, productions, mainRole, seniority }));
  }, 300);
  return () => clearTimeout(timer);
}, [profile, prodList]);


  const activeProduction = useMemo(
    () => prodList.find(p => isActiveContract(p.data_fine_contratto)) || null,
    [prodList]
  );
  const activeProductions = useMemo(
    () => prodList.filter(p => isActiveContract(p.data_fine_contratto)),
    [prodList]
  );
  const otherProductions = useMemo(
    () => prodList
      .filter(p => !isActiveContract(p.data_fine_contratto))
      .slice()
      .sort((a, b) => {
     const ae = toDateOnly(a?.data_fine_contratto) || '0000-00-00';
     const be = toDateOnly(b?.data_fine_contratto) || '0000-00-00';
     return ae < be ? 1 : (ae > be ? -1 : 0); // desc
    }),
    [prodList]
  );

  // Set dei giorni occupati: usa la prop se è già un Set,
// altrimenti calcola dalle produzioni del profilo (maestranze/produzioni)
const busyDaysSet = useMemo(() => {
  // Se arriva già un Set via prop, ignoralo e ricalcola filtrando solo produzioni attive/future
  // (le produzioni passate non devono marcare giorni come occupati)
  const todayStr = new Date().toISOString().slice(0, 10);
  const activeProd = (Array.isArray(prodList) ? prodList : []).filter(p => {
    const fine = p?.data_fine_contratto || p?.data_fine || p?.endDate || '';
    return fine && String(fine).slice(0, 10) >= todayStr;
  });
  if (activeProd.length > 0) return busyDaysFromProduzioni(activeProd.filter(p => !p.is_tentative)).set;
  // fallback: se arriva già un Set via prop, usalo
  if (busyDays instanceof Set) return busyDays;

  // Prendi le produzioni dal profilo in qualunque chiave siano
  const raw =
    profile?.maestranze ??
    profile?.produzioni ??
    profile?.productions ??
    [];

  try {
    return buildBusySetFromProduzioni(Array.isArray(raw) ? raw : []);
  } catch {
    return new Set();
  }
}, [busyDays, profile]);
  const tentativeDaysSet = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const activeProd = (Array.isArray(prodList) ? prodList : []).filter(p => {
      const fine = p?.data_fine_contratto || p?.data_fine || p?.endDate || "";
      return fine && String(fine).slice(0, 10) >= todayStr;
    });
    if (activeProd.length > 0) {
      const result = busyDaysFromProduzioni(activeProd).tentativeSet || new Set();
      return result;
    }
    return new Set();
  }, [prodList]);



  const toggleAvailability = async () => {
    if (readOnly || !profile) return;
    const next = !isAvailable;
    setSavingAvail(true);
    try {
      const res = await fetch(`${API_BASE}${endpointAvailability}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ is_available: next }),
      });
      if (!res.ok) throw new Error('Update availability failed');
      setProfile(prev => ({ ...prev, is_available: next }));
    } catch (e) {
      console.error(e);
    } finally {
      setSavingAvail(false);
    }
  };

  // === Team Mini Panel: gestione "Cerca ruolo" ===
const handleFindRole = (roleLabel) => {
  try {
    const q = encodeURIComponent(roleLabel);
    window.location.assign(`/freelancers/search?role=${q}`);
  } catch (e) {
    console.error('Errore durante la ricerca del ruolo:', e);
  }
};

const [teamBuilderOpen, setTeamBuilderOpen] = React.useState(false);
const openTeamBuilder = () => setTeamBuilderOpen(true);
const closeTeamBuilder = () => setTeamBuilderOpen(false);



  if (!profile) return null;

  const daysToEnd = activeProduction ? daysUntil(activeProduction.data_fine_contratto) : Infinity;
  const soonAvailable = daysToEnd > 0 && daysToEnd <= 14; // soglia 14gg

  return (
    <>
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 3, backgroundColor: '#13161c', borderColor: 'rgba(212,165,80,0.2)' }}>
      <CardContent>
        {/* ALERT REALI PER MACROGRUPPO — SOLO DASHBOARD FREELANCE */}
        {!readOnly && (
          <Box sx={{ mb: 2 }}>
            <FreelancerAlertsPanel />
          </Box>
        )}

        <Box
  sx={{
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
    gap: 4,
    alignItems: 'start'
  }}
>

          {/* COLONNA SINISTRA ------------------------------------------------ */}
                  <Stack alignItems="center" spacing={1.25}>
            {/* Avatar grande con fallback iniziali */}
{/* Avatar grande con fallback */}
<Box sx={{ position: 'relative', width: 144, height: 144, mb: 1 }}>
  {avatarUrl && !imgError ? (
    <Box
      component="img"
      src={(avatarUrl || '').replace('localhost:5000', 'localhost:3001')}
      alt={displayName}
      onError={() => setImgError(true)}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        objectFit: 'cover',
        boxShadow: 2,
        border: '2px solid #fff',
        display: 'block',
      }}
    />
  ) : (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        bgcolor: 'grey.200',
        color: 'text.secondary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 54,
        fontWeight: 600,
      }}
    >
      {`${profile?.nome?.[0] ?? ''}${profile?.cognome?.[0] ?? ''}`
        .trim()
        .toUpperCase() || 'R'}
    </Box>
  )}
</Box>


            <Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>{displayName}</Typography>

{headline && (
  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
    {headline}
  </Typography>
)}





            {phone && !readOnly && (
              <Typography variant="body2" sx={{ color:'text.secondary' }}>
  Telefono: <Box component="span" sx={{ fontWeight: 800, letterSpacing:.3 }}>
    {formatPhonePretty(profile?.telefono)}
  </Box>
</Typography>

            )}

            {/* STATO -------------------------------------------------------- */}
            {readOnly ? (
              // Vista pubblica (case di produzione): stato da contratto
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                  label={activeProduction ? 'OCCUPATO' : 'DISPONIBILE'}
                  variant="filled"
                  sx={{
                    fontWeight: 700, fontSize: 11, letterSpacing: '0.08em',
                    bgcolor: activeProduction ? 'rgba(239,83,80,0.15)' : 'rgba(76,175,80,0.15)',
                    color: activeProduction ? '#ef9a9a' : '#81c784',
                    border: `1px solid ${activeProduction ? 'rgba(239,83,80,0.3)' : 'rgba(76,175,80,0.3)'}`,
                  }}
                />
                {activeProduction && soonAvailable && (
                  <Chip
                    label={`Presto disponibile (${daysToEnd}gg)`}
                    size="small"
                    variant="filled"
                    sx={{
                      fontSize: 11, fontWeight: 600,
                      bgcolor: 'rgba(212,165,80,0.12)',
                      color: '#d4a550',
                      border: '1px solid rgba(212,165,80,0.3)',
                    }}
                  />
                )}
              </Box>
            ) : (
              // Vista utente (dashboard): ONLINE/OFFLINE indipendente
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                  label={isAvailable ? 'ONLINE' : 'OFFLINE'}
                  color={isAvailable ? 'success' : 'default'}
                  variant={isAvailable ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700 }}
                />
                <Tooltip title="Renditi visibile nelle ricerche">
                  <Switch checked={isAvailable} onChange={toggleAvailability} disabled={savingAvail} />
                </Tooltip>
              </Box>
            )}

            {/* CONTATTI ----------------------------------------------------- */}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              {/* Messaggio interno (prioritario) */}
              <Button
                size="small"
                variant="contained"
                onClick={onMessage}
                disabled={!onMessage}
                sx={{ borderRadius: 999, fontWeight: 800, textTransform: "none" }}
              >
                Messaggio
              </Button>

              {/* Contatti “secondari”: io li lascerei solo se NON readOnly (cioè quando è il proprietario del profilo) */}
            </Stack>

            {/* Calendario “classico” dentro la colonna sinistra del profilo */}
<div style={{ marginTop: 12 }}>
  <div style={{ fontSize:12, color:'#6b7280', marginBottom:6 }}>Calendario disponibilità</div>
<MiniAvailabilityCalendar busyDays={busyDaysSet} tentativeDays={tentativeDaysSet} />
</div>




            {collaborationsSlot && <Box sx={{ mt: 2 }}>{collaborationsSlot}</Box>}
            {/* MODIFICA PROFILO -------------------------------------------- */}
            {!readOnly && !!onEditProfile && (
              <Button size="small" variant="text" startIcon={<EditIcon />} onClick={onEditProfile} sx={{ mt: 0.5 }}>
                Modifica profilo
              </Button>
            )}
          </Stack>

          {/* COLONNA DESTRA ------------------------------------------------- */}
          <Box>


            {/* BIO dentro la stessa gabbia di SKILL/PRODUZIONI */}
<Typography variant="overline" sx={{ letterSpacing: 1, color: 'text.secondary' }}>
  BIO
</Typography>

{bioText ? (
  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
    {bioText}
  </Typography>
) : (
  <Typography variant="body2" sx={{ color: 'text.disabled', mt: 0.5 }}>
    Nessuna bio presente.
  </Typography>
)}

<Divider sx={{ my: 2 }} />



            {/* SKILL come pill carine */}
            <Typography variant="overline" sx={{ letterSpacing: 1, color: 'text.secondary' }}>SKILL</Typography>
            {skills.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ my: 1 }}>
                {skills.map((s, i) => (
                  <Chip
                  key={`${s}-${i}`}
                  label={s}
                  size="small"
                  variant="outlined"
                  sx={{
                    px: 1.2,
                    height: 28,
                    borderRadius: '20px',
                    borderWidth: 1,
                    borderColor: 'rgba(255, 152, 0, 0.5)',  // tono coerente col giallo MUI warning
                    color: 'rgba(255, 152, 0, 0.9)',
                    fontWeight: 500,
                    bgcolor: 'rgba(255, 152, 0, 0.05)',      // leggero sfondo per equilibrio visivo
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0.3,
                    },
                  }}
                />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Nessuna skill indicata.
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* PRODUZIONI (timeline stile mockup) --------------------------- */}
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>PRODUZIONI</Typography>

            <Box sx={{ position: 'relative', pl: 3, mt: 1 }}>

              {/* Disegno elenco: prima attiva (se c'è), poi le altre */}
              {[...activeProductions, ...otherProductions].filter(Boolean).slice(0, showAllProds ? 999 : 5).map((p, idx, arr) => {
                const isActive = activeProductions.includes(p);

                // Credito CV collegato a questa voce maestranza
                const credit = (creditsMap && p.id != null) ? creditsMap[String(p.id)] : undefined;
                const creditLabel = credit
                  ? [credit.program_name, credit.edition_label].filter(Boolean).join(' · ed. ')
                  : null;
                const rawTitle = (p.produzione || p.title || p.titolo || p.nome || p.name || '').toLowerCase().trim();
                const showCanonical = creditLabel && credit.program_name &&
                  rawTitle !== credit.program_name.toLowerCase().trim();
                const teamCount = credit?.team_count ?? 0;

return (
                  <Box key={p.id ?? `${p.produzione}-${p.data_fine_contratto || idx}`} sx={{ position: 'relative', mb: 2, pl: 3 }}>
                    {/* Pallino */}
                    <Box sx={{
                      position: 'absolute', left: -3, top: 8,
                      width: 10, height: 10, borderRadius: '50%',
                      bgcolor: isActive ? 'warning.main' : 'grey.400'
                    }} />
                    {/* Linea verticale */}
{idx < arr.length - 1 && (
  <Box
    sx={{
      position: 'absolute',
      left: 1,           // centrato rispetto al pallino
      top: 18,           // parte sotto al cerchio
      bottom: -24,       // arriva fino all'item successivo
      width: 2,
      bgcolor: 'divider',
    }}
  />
)}

                    {/* Riga orizzontale dal pallino al contenuto */}
                    <Box sx={{
                      position: 'absolute', left: 7, top: 12,
                      height: 2, width: 20, bgcolor: isActive ? 'primary.main' : 'grey.400'
                    }} />

                    {/* Contenuto a destra della riga */}
                    <Box sx={{ ml: 5 }}>
                      {/* Azienda */}
                      {p.azienda && (
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: 'primary.main',
                            fontSize: '0.95rem',
                            mb: 0.5
                          }}
                        >
                          {p.azienda}
                        </Typography>
                      )}

                      {/* Titolo programma: canonico se disponibile, grezzo altrimenti */}
                      <Typography
                        sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3 }}
                        title={showCanonical ? t('crediti.inserito_come', { text: p.produzione || p.title || p.titolo || p.nome || p.name || '' }) : undefined}
                      >
                        {credit ? creditLabel : (p.title || p.produzione)}
                        {(p.roleForMe) && (
                          <Typography component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', fontWeight: 400 }}>
                            {' '}| {p.roleForMe}
                          </Typography>
                        )}
                      </Typography>
                      {p.is_tentative && (
                        <Box component="span" sx={{ display:'inline-block', mt:0.5, fontSize:10, fontWeight:700, color:'#a78bfa', background:'rgba(167,139,250,0.12)', border:'1px solid rgba(167,139,250,0.35)', borderRadius:10, px:1, py:0.25 }}>
                          {t('crediti.non_confermata', 'Non confermata')}
                        </Box>
                      )}

                     {/* DATE (range inizio → fine, con fallback) */}
{(() => {
  const startRaw = p?.data_inizio_contratto || p?.inizio_contratto || p?.data_inizio;
  const endRaw   = p?.data_fine_contratto   || p?.fine_contratto   || p?.data_fine;

  const endAdjYmd = toDateOnly(endRaw);
  const startStr = formatIT(startRaw);
  const endStr   = formatIT(endAdjYmd);

  const dateText = startStr && endStr
    ? `${startStr} → ${endStr}`
    : (endStr || startStr || '');

  return dateText ? (
    <Typography variant="caption" sx={{ color:'text.secondary', mt: 0.25, display:'block' }}>
      {dateText}
    </Typography>
  ) : null;
})()}

                      {/* Badge di verifica + bottone Vedi squadra */}
                      {credit && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          {credit.role_verified_by === 'production' && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
                              background: 'rgba(212,165,80,0.13)', border: '1px solid rgba(212,165,80,0.28)',
                              color: '#d4a550', whiteSpace: 'nowrap', letterSpacing: '0.04em',
                            }}>
                              {t('crediti.verified_production')}
                            </span>
                          )}
                          {credit.role_verified_by === 'coordinator' && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
                              background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.3)',
                              color: '#94a3b8', whiteSpace: 'nowrap', letterSpacing: '0.04em',
                            }}>
                              {t('crediti.verified_coordinator')}
                            </span>
                          )}
                          {teamCount >= 1 && (
                            <Box
                              component="span"
                              onClick={() => handleOpenSquadra(credit)}
                              sx={{
                                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                color: 'rgba(212,165,80,0.7)',
                                border: '1px solid rgba(212,165,80,0.28)',
                                borderRadius: '6px', px: 1, py: 0.25,
                                '&:hover': { color: '#d4a550', background: 'rgba(212,165,80,0.08)' },
                              }}
                            >
                              {teamCount > 1
                                ? t('crediti.vedi_squadra_n', { count: teamCount })
                                : t('crediti.vedi_squadra')}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {(!activeProduction && otherProductions.length === 0) && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Nessuna produzione inserita.
                </Typography>
              )}
              {[...activeProductions, ...otherProductions].filter(Boolean).length > 5 && !showAllProds && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Box component="span"
                    onClick={() => setShowAllProds(true)}
                    sx={{ fontSize: 12, color: '#d4a550', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Mostra tutte le produzioni ({[...activeProductions, ...otherProductions].filter(Boolean).length - 5} altre) ↓
                  </Box>
                </Box>
              )}
            </Box>
                    </Box>

        </Box> {/* <-- chiusura GRIGLIA */}

          

      </CardContent>
      </Card>

      {/* Modal "Vedi squadra" — sola lettura, renderizzato fuori dal Card per z-index corretto */}
      <SquadraModal
        open={squadraOpen}
        onClose={() => setSquadraOpen(false)}
        editionId={squadraEditionId}
        programName={squadraProgramName}
        editionLabel={squadraEditionLabel}
      />
    </>
  );
}
